import React from 'react'
import { Link } from 'react-router-dom'

// material ui
import Box from '@mui/material/Box'
import FormLabel from '@mui/material/FormLabel'
import FormHelperText from '@mui/material/FormHelperText'
import Select from '@mui/material/Select'
import MenuItem from '@mui/material/MenuItem'
import RadioGroup from '@mui/material/RadioGroup'
import Checkbox from '@mui/material/Checkbox'
import FormControlLabel from '@mui/material/FormControlLabel'
import Radio from '@mui/material/Radio'
import Typography from '@mui/material/Typography'
import MUILink from '@mui/material/Link'

// components
import FormCard from 'components/atoms/FormCard'
import FormButton from 'components/atoms/FormButton'
import ErrorMessage from 'components/shared/ErrorMessage'
import FormTextFieldWithValidation from 'components/shared/FormTextFieldWithValidation'
import PasswordFieldWithRequirements from 'components/shared/PasswordFieldWithRequirements'
import FormSelectWithValidation from 'components/shared/FormSelectWithValidation'
import FormControlWithErrorHelper from 'components/shared/FormControlWithErrorHelper'
import RegionInput from 'components/shared/CountryAwareRegionInput'
import { useBrand } from 'components/shared/SettingsProvider'

import {
  relationshipToCampersOptions,
  phoneTypeOptions
} from './selectionOptions'

const ParentForm = props => {
  const {
    marcomUrl,
    shortName,
    contact: { sms }
  } = useBrand()
  const {
    onChangeFormField, // via ParentFormContainer/withEventHandlers
    parent, // via EditParentContainer/withQuery
    states, // via EditParentContainer/withQuery
    countries, // via EditParentContainer/withQuery
    form, // via EditParentContainer/withRedux
    mutationLoading, // via EditParentContainer/withMutation
    handleSubmit, // via EditParentContainer/withEventHandlers
    primary, // true if this is the primary account holder
    redirect,
    formErrors, // via withFormResults/withState
    buttonText,
    turnstileComponent, // via NewAccount/UI
    passwordRequirementsMet, // via NewAccount/withState
    showFormFields = true
  } = props

  // We trust the smsOptIn field if it's true or if there is a value in smsOptedAt.
  // smsOptIn defaults to false and we want to know if it was intentionally set to false
  const smsOptIn =
    !!form.smsOptedAt || form.smsOptIn ? form.smsOptIn.toString() : null

  return (
    <FormCard>
      <ErrorMessage />
      {showFormFields && (
        <form>
          <FormTextFieldWithValidation
            label='First Name'
            field='firstName'
            form={form}
            onChange={onChangeFormField}
            errors={formErrors}
            required
          />

          <FormTextFieldWithValidation
            label='Last Name'
            field='lastName'
            form={form}
            onChange={onChangeFormField}
            errors={formErrors}
            required
          />

          <FormControlWithErrorHelper
            required
            errorsObject={formErrors}
            errorField='relationshipToCampers'
          >
            <FormLabel htmlFor='relationshipToCampers'>
              Relationship To Child/Children
            </FormLabel>
            <Select
              id='relationshipToCampers'
              value={form.relationshipToCampers}
              onChange={onChangeFormField('relationshipToCampers')}
              variant='outlined'
              displayEmpty
              fullWidth
              name='relationshipToCampers'
              inputProps={{
                name: 'relationshipToCampers',
                id: 'relationshipToCampers'
              }}
            >
              {relationshipToCampersOptions.map(option => (
                <MenuItem key={option} value={option.toLowerCase()}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControlWithErrorHelper>

          <FormControlWithErrorHelper
            component='fieldset'
            required
            errorsObject={formErrors}
            errorField='legalGuardian'
          >
            <FormLabel component='legend' htmlFor='legalGuardian'>
              Is this adult a Legal Guardian?
            </FormLabel>
            <RadioGroup
              id='legalGuardian'
              value={form.legalGuardian}
              onChange={onChangeFormField('legalGuardian')}
              aria-label='legalGuardian'
              name='legalGuardian'
            >
              <FormControlLabel value='true' control={<Radio />} label='Yes' />
              <FormControlLabel value='false' control={<Radio />} label='No' />
            </RadioGroup>
          </FormControlWithErrorHelper>

          <FormTextFieldWithValidation
            label='Street Address'
            field='streetAddress'
            form={form}
            onChange={onChangeFormField}
            errors={formErrors}
            required
          />

          <FormTextFieldWithValidation
            label='City'
            field='city'
            form={form}
            onChange={onChangeFormField}
            errors={formErrors}
            required
          />

          <FormSelectWithValidation
            label='Country'
            form={form}
            field='country'
            options={countries.map(country => [country.alpha2, country.name])}
            onChange={onChangeFormField}
            errors={formErrors}
            required
          />

          <RegionInput
            onChangeFormField={onChangeFormField}
            states={states}
            form={form}
            formErrors={formErrors}
          />

          <FormTextFieldWithValidation
            label={form.country === 'US' ? 'Zip Code' : 'Postal Code'}
            field='postalCode'
            form={form}
            onChange={onChangeFormField}
            errors={formErrors}
            required
          />

          <FormTextFieldWithValidation
            label='Daytime Phone (with area code)'
            field='primaryPhone'
            form={form}
            onChange={onChangeFormField}
            errors={formErrors}
            required
          >
            <FormHelperText>
              Fastest way to reach Parent/Guardian during business hours.
            </FormHelperText>
            <FormHelperText>International: +## ### ### ###</FormHelperText>
          </FormTextFieldWithValidation>

          <FormSelectWithValidation
            label='Daytime Phone Type'
            form={form}
            field='primaryPhoneType'
            options={phoneTypeOptions.map(phoneType => [
              phoneType.toLowerCase(),
              phoneType
            ])}
            onChange={onChangeFormField}
            errors={formErrors}
            required
            sx={{ height: '40px', width: '60%' }}
          />

          <FormControlWithErrorHelper
            component='fieldset'
            required
            errorsObject={formErrors}
            errorField='smsOptIn'
          >
            <FormLabel component='legend' htmlFor='smsOptIn'>
              Opt in for SMS Messaging?
            </FormLabel>
            <RadioGroup
              id='smsOptIn'
              value={smsOptIn}
              onChange={onChangeFormField('smsOptIn')}
              aria-label='smsOptIn'
              name='smsOptIn'
            >
              <FormControlLabel value='true' control={<Radio />} label='Yes' />
              <FormControlLabel value='false' control={<Radio />} label='No' />
            </RadioGroup>
            <FormHelperText>
              By selecting Yes you consent to receiving SMS messages about your
              account and attendances. You may receive up to one message per
              week. Standard message and data rates may apply. By signing up you
              agree to Steve and Kate's Camp's{' '}
              <MUILink
                href={`${marcomUrl}/sms-terms-conditions`}
                target='_blank'
                sx={{ opacity: '0.6' }}
              >
                Terms and Conditions
              </MUILink>{' '}
              and{' '}
              <MUILink
                href={`${marcomUrl}/privacy-policy`}
                target='_blank'
                sx={{ opacity: '0.6' }}
              >
                Privacy Policy
              </MUILink>
              . By selecting NO you understand that you will not receive any
              text communications including emergency texts. For assistance text
              HELP, or to unsubscribe text STOP to {sms}.
            </FormHelperText>
          </FormControlWithErrorHelper>

          <FormTextFieldWithValidation
            label='Alternate Phone (with area code)'
            field='alternatePhone'
            form={form}
            onChange={onChangeFormField}
            errors={formErrors}
          >
            <FormHelperText>International: +## ### ### ###</FormHelperText>
          </FormTextFieldWithValidation>

          <FormTextFieldWithValidation
            label='Employer'
            field='employerName'
            form={form}
            onChange={onChangeFormField}
            errors={formErrors}
          />

          <FormTextFieldWithValidation
            label='Email'
            field='email'
            form={form}
            onChange={onChangeFormField}
            errors={formErrors}
            required
          >
            <FormHelperText>
              Only the primary account holder will receive automatic
              notifications.
            </FormHelperText>
          </FormTextFieldWithValidation>

          {primary && (
            <>
              <PasswordFieldWithRequirements
                label='Password'
                field='password'
                form={form}
                onChange={onChangeFormField}
                errors={formErrors}
                required
              />

              <FormTextFieldWithValidation
                label='Verify Password'
                field='passwordConfirmation'
                type='password'
                form={form}
                onChange={onChangeFormField}
                errors={formErrors}
                required
              />
            </>
          )}

          {!primary && (!parent || !parent.primaryAccountHolder) && (
            <FormControlWithErrorHelper
              component='fieldset'
              errorsObject={formErrors}
              errorField='accountAccess'
              required
            >
              <FormLabel htmlFor='accountAccess' component='legend'>
                Grant Account Access?
              </FormLabel>
              <FormHelperText>
                {shortName}'s standard policy is to only allow the primary
                account holder to request or make account changes or request
                account information. If you would like to authorize another
                parent / guardian to request or make account changes or request
                account information, please select "Yes". (Please note: In some
                states and under some circumstances, we may be required by law
                to share information about your account with another parent /
                guardian regardless of your selection here.)
              </FormHelperText>
              <RadioGroup
                id='accountAccess'
                value={
                  form.accountAccess != null && form.accountAccess.toString()
                }
                onChange={onChangeFormField('accountAccess')}
                aria-label='account access'
                name='accountAccess'
              >
                <FormControlLabel
                  value='true'
                  control={<Radio />}
                  label='Yes'
                />
                <FormControlLabel
                  value='false'
                  control={<Radio />}
                  label='No'
                />
              </RadioGroup>
            </FormControlWithErrorHelper>
          )}

          {!primary && (
            <FormControlWithErrorHelper
              component='fieldset'
              required
              errorsObject={formErrors}
              errorField='pickup'
            >
              <FormLabel component='legend' htmlFor='pickup'>
                Authorized for pick-up?
              </FormLabel>
              <RadioGroup
                id='pickup'
                value={form.pickup != null && form.pickup.toString()}
                onChange={onChangeFormField('pickup')}
                aria-label='pickup'
                name='pickup'
              >
                <FormControlLabel
                  value='true'
                  control={<Radio />}
                  label='Yes'
                />
                <FormControlLabel
                  value='false'
                  control={<Radio />}
                  label='No'
                />
              </RadioGroup>
            </FormControlWithErrorHelper>
          )}

          {turnstileComponent && (
            <>
              <FormLabel sx={{ mt: 2 }} component='legend' htmlFor='pickup'>
                Verify you're a human
              </FormLabel>
              <Box py={2}>{turnstileComponent}</Box>
            </>
          )}

          <Box py={2} align='center'>
            {redirect && (
              <FormButton variant='outlined' component={Link} to={redirect}>
                Cancel
              </FormButton>
            )}

            <FormButton
              variant='contained'
              color='secondary'
              disabled={
                mutationLoading ||
                (primary && passwordRequirementsMet === false)
              }
              onClick={handleSubmit}
            >
              {buttonText}
            </FormButton>
          </Box>
        </form>
      )}
    </FormCard>
  )
}

export default ParentForm
